import { useState, useEffect, useCallback, useRef } from 'react'
import { api, useAuth } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const rwf   = (n) => `RWF ${Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
const pct   = (n) => `${Number(n ?? 0).toFixed(1)}%`
const num   = (n) => Number(n ?? 0).toLocaleString()
const toISO = (d) => d.toISOString().slice(0, 10)
const fmtD  = (iso) => { const [y,m,d] = iso.split('-'); return `${d}/${m}/${y}` }

function buildPresets() {
  const now = new Date(); const y = now.getFullYear(); const mo = now.getMonth()
  const ld  = (yr, mn) => new Date(yr, mn + 1, 0).getDate()
  const mn  = (yr, mn) => new Date(yr, mn, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  return [
    { label: 'This month',   from: `${y}-${String(mo+1).padStart(2,'0')}-01`, to: toISO(now) },
    { label: mn(y, mo-1),    from: toISO(new Date(y,mo-1,1)), to: toISO(new Date(y,mo-1,ld(y,mo-1))) },
    { label: mn(y, mo-2),    from: toISO(new Date(y,mo-2,1)), to: toISO(new Date(y,mo-2,ld(y,mo-2))) },
    { label: 'Last 90 days', from: toISO(new Date(now - 90*864e5)), to: toISO(now) },
  ]
}

// ── Report catalogue ──────────────────────────────────────────────────────────
const REPORTS = [
  { id:'pnl',       icon:'💰', title:'P&L Summary',          desc:'Gross revenue, fees & net payout',               color:'#15803d', bg:'#f0fdf4', border:'#bbf7d0' },
  { id:'sales',     icon:'📈', title:'Sales Performance',     desc:'Revenue trend, orders & avg order value',        color:'#e8440a', bg:'#fff3f0', border:'#fdd0c0' },
  { id:'products',  icon:'🏆', title:'Product Performance',   desc:'Best sellers, slow movers & revenue per SKU',    color:'#7c3aed', bg:'#f5f3ff', border:'#ddd6fe' },
  { id:'orders',    icon:'📦', title:'Order Health',          desc:'Cancellation rate, fulfilment & payment success', color:'#0369a1', bg:'#f0f9ff', border:'#bae6fd' },
  { id:'delivery',  icon:'🚗', title:'Delivery Report',       desc:'Driver performance, failures & delivery time',   color:'#b45309', bg:'#fffbeb', border:'#fde68a' },
  { id:'earnings',  icon:'🏦', title:'Earnings & Payout',     desc:'Per-order payout, fees, tips & commissions',     color:'#1d4ed8', bg:'#eff6ff', border:'#bfdbfe' },
  { id:'inventory', icon:'🗃', title:'Inventory Report',      desc:'Stock levels, low-stock & restocking alerts',    color:'#374151', bg:'#f9fafb', border:'#e5e7eb' },
  { id:'live',      icon:'🔴', title:'Live Streaming',        desc:'Stream performance, viewers & engagement',       color:'#b91c1c', bg:'#fef2f2', border:'#fecaca' },
]

// ── Reusable primitives ───────────────────────────────────────────────────────
function Stat({ label, value, sub, accent, bold }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'16px 18px', flex:1, minWidth:140 }}>
      <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace", marginBottom:7 }}>{label}</div>
      <div style={{ fontSize:bold?24:22, fontWeight:800, letterSpacing:'-0.5px', color:accent||'#111317' }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:'#9a9a94', marginTop:4, fontFamily:"'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function BarRow({ label, value, max, color, sub }) {
  const w = max > 0 ? Math.max(2, Math.round(value / max * 100)) : 0
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
        <span style={{ fontSize:12, fontWeight:600, color:'#374151' }}>{label}</span>
        <span style={{ fontSize:12, fontWeight:800, fontFamily:"'JetBrains Mono',monospace" }}>{typeof value==='number' && value>999 ? rwf(value) : value}</span>
      </div>
      <div style={{ height:7, background:'#f3f3f0', borderRadius:4, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${w}%`, background:color, borderRadius:4, transition:'width 0.4s' }} />
      </div>
      {sub && <div style={{ fontSize:10, color:'#9a9a94', marginTop:2, fontFamily:"'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function MiniChart({ data, color='#e8440a', label }) {
  if (!data?.length) return null
  const max = Math.max(...data.map(r=>r.value), 1)
  return (
    <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'16px 18px' }}>
      {label && <div style={{ fontSize:12, fontWeight:700, marginBottom:12 }}>{label}</div>}
      <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:70 }}>
        {data.map((r,i) => (
          <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
            <div title={rwf(r.value)} style={{ width:'100%', background:color, borderRadius:'2px 2px 0 0', opacity:0.85,
              height:`${Math.max(3, Math.round(r.value/max*56))}px`, transition:'height 0.3s' }} />
            <span style={{ fontSize:8, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", overflow:'hidden', maxWidth:'100%', textAlign:'center' }}>{r.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Table({ cols, rows, emptyMsg='No data.' }) {
  if (!rows?.length) return <div style={{ textAlign:'center', padding:'28px 0', fontSize:13, color:'#9a9a94' }}>{emptyMsg}</div>
  return (
    <div style={{ border:'1px solid #e9e9e4', borderRadius:10, overflow:'hidden' }}>
      <div style={{ display:'grid', gridTemplateColumns:cols.map(c=>c.w||'1fr').join(' '), padding:'9px 16px', background:'#f9f9f7', borderBottom:'1px solid #e9e9e4' }}>
        {cols.map(c => <div key={c.key} style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace" }}>{c.label}</div>)}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:cols.map(c=>c.w||'1fr').join(' '), padding:'10px 16px', borderBottom:i<rows.length-1?'1px solid #f3f3f0':'none', alignItems:'center' }}>
          {cols.map(c => (
            <div key={c.key} style={{ fontSize:12, fontFamily:c.mono?"'JetBrains Mono',monospace":'inherit', fontWeight:c.bold?700:500, color:c.color?c.color(row):undefined, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {c.render ? c.render(row) : row[c.key]}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// ── PDF export ────────────────────────────────────────────────────────────────
function printReport(title, dateFrom, dateTo) {
  window.document.title = `${title} — ${fmtD(dateFrom)} to ${fmtD(dateTo)}`
  window.print()
}

// ── CSV helpers ───────────────────────────────────────────────────────────────
function downloadCSV(filename, rows) {
  const esc = (v) => `"${String(v??'').replace(/"/g,'""')}"`
  const csv = rows.map(r => (Array.isArray(r) ? r.map(esc).join(',') : r)).join('\n')
  const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href=url; a.download=filename; a.click()
  URL.revokeObjectURL(url)
}

// ── Date range controls (shared) ─────────────────────────────────────────────
function DateRange({ dateFrom, dateTo, onFrom, onTo, presets, activePreset, onPreset }) {
  const INP = { height:36, border:'1px solid #e9e9e4', borderRadius:8, padding:'0 10px', fontSize:13, outline:'none', fontFamily:"'JetBrains Mono',monospace", color:'#111317', background:'#fff' }
  return (
    <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'12px 16px', marginBottom:22, display:'flex', alignItems:'flex-end', gap:16, flexWrap:'wrap' }}>
      <div>
        <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:4 }}>From</div>
        <input type="date" value={dateFrom} max={dateTo} onChange={onFrom} style={INP} />
      </div>
      <div>
        <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:4 }}>To</div>
        <input type="date" value={dateTo} min={dateFrom} max={toISO(new Date())} onChange={onTo} style={INP} />
      </div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignSelf:'flex-end' }}>
        {presets.map((p,i) => (
          <button key={i} onClick={()=>onPreset(i)}
            style={{ height:36, padding:'0 12px', borderRadius:8, border:`1px solid ${activePreset===i?'#111317':'#e9e9e4'}`, background:activePreset===i?'#111317':'#fff', color:activePreset===i?'#fff':'#374151', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// INDIVIDUAL REPORT VIEWS
// ═══════════════════════════════════════════════════════════════════════

// ── P&L Summary ──────────────────────────────────────────────────────────────
function ReportPNL({ dateFrom, dateTo }) {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true)
  useEffect(() => {
    setLoading(true)
    Promise.all([
      api.get(`/analytics/shop?date_from=${dateFrom}&date_to=${dateTo}`),
      api.get(`/orders/earnings-detail?date_from=${dateFrom}&date_to=${dateTo}`),
    ]).then(([a,e]) => setData({ s: a.data.summary||{}, e: e.data.totals||{},
        tier: e.data.tier ?? 'bronze',
        platBase: e.data.platform_pct_base ?? 0,
        platEff:  e.data.platform_pct_effective ?? 0,
        tierDisc: e.data.tier_discount ?? 0,
        isOverridden: e.data.is_overridden ?? false,
    }))
      .catch(()=>{}).finally(()=>setLoading(false))
  }, [dateFrom, dateTo])

  if (loading) return <Loader />
  const { s, e, tier, platBase, platEff, tierDisc, isOverridden } = data || { s:{}, e:{} }
  const fees       = (s.ipay_fee??0)+(s.platform_fee??0)+(e.delivery_commission??0)
  const margin     = s.revenue > 0 ? ((s.total_payout??0)/s.revenue*100) : 0
  const tierSaving = +(Number(e.gross_subtotal||s.revenue||0) * (tierDisc||0)).toFixed(2)
  const PNL_TIER_ICONS = { bronze:'🥉', silver:'🥈', gold:'🥇', platinum:'💎' }

  const csv = () => downloadCSV(`pnl-${dateFrom}-${dateTo}.csv`, [
    ['P&L SUMMARY', `${fmtD(dateFrom)} – ${fmtD(dateTo)}`],
    [],
    ['Metric','Amount (RWF)'],
    ['Tier',                 `${PNL_TIER_ICONS[tier]||''} ${tier||'bronze'}`.trim()],
    ['Platform Fee % (Base)',   `${((platBase||0)*100).toFixed(0)}%`],
    ['Platform Fee % (Effective)', `${((platEff||0)*100).toFixed(0)}%`],
    ...(tierDisc > 0 ? [['Tier Saving (RWF)', tierSaving]] : []),
    ['Gross Revenue',        s.revenue          ?? 0],
    ['iPay Fee',             s.ipay_fee         ?? 0],
    ['Platform Fee',         s.platform_fee     ?? 0],
    ['Delivery Commission',  e.delivery_commission??0],
    ['Total Fees',           fees],
    ['Product Payout',       e.product_payout   ?? 0],
    ['Delivery Gross',       e.delivery_gross   ?? 0],
    ['Delivery Payout',      e.delivery_payout  ?? 0],
    ['Tips',                 e.tips_total       ?? 0],
    ['NET PAYOUT',           s.total_payout     ?? 0],
    ['Net Margin (%)',        margin.toFixed(2)  + '%'],
  ])

  return (
    <div id="report-content">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Gross Revenue"  value={rwf(s.revenue)}           accent="#111317" bold />
        <Stat label="Total Fees"     value={rwf(fees)}                accent="#b91c1c" sub={`iPay + Platform + Delivery comm.`} />
        <Stat label="Net Payout"     value={rwf(s.total_payout??0)}   accent="#15803d" bold />
        <Stat label="Net Margin"     value={pct(margin)}              accent={margin>=60?'#15803d':margin>=40?'#b45309':'#b91c1c'} />
        {tierDisc > 0 && (
          <Stat label={`${PNL_TIER_ICONS[tier]||''} Tier Saving`.trim()} value={rwf(tierSaving)}
            accent="#15803d"
            sub={`${((platEff||0)*100).toFixed(0)}% fee vs ${((platBase||0)*100).toFixed(0)}% base${isOverridden?' · admin set':''}`} />
        )}
      </div>

      <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'18px 20px', marginBottom:20 }}>
        <div style={{ fontSize:13, fontWeight:800, marginBottom:14 }}>Revenue to Payout Waterfall</div>
        {[
          { label:'Gross Revenue',        value:s.revenue??0,            color:'#111317' },
          { label:'− iPay Fee',           value:s.ipay_fee??0,           color:'#ef4444' },
          { label:`− Platform Fee (${((platEff||0)*100).toFixed(0)}%)`, value:s.platform_fee??0, color:'#f97316' },
          ...(tierDisc > 0 ? [{ label:`+ Tier Saving (${((tierDisc||0)*100).toFixed(0)}pp off)`, value:tierSaving, color:'#15803d' }] : []),
          { label:'+ Delivery Payout',    value:e.delivery_payout??0,    color:'#0ea5e9' },
          { label:'+ Tips',               value:e.tips_total??0,         color:'#7c3aed' },
        ].map(r => <BarRow key={r.label} label={r.label} value={r.value} max={s.revenue||1} color={r.color} />)}
        <div style={{ borderTop:'2px solid #111317', paddingTop:12, marginTop:4, display:'flex', justifyContent:'space-between' }}>
          <span style={{ fontWeight:800, fontSize:14 }}>NET PAYOUT</span>
          <span style={{ fontWeight:800, fontSize:15, fontFamily:"'JetBrains Mono',monospace", color:'#15803d' }}>{rwf(s.total_payout??0)}</span>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'16px 18px' }}>
          <div style={{ fontSize:12, fontWeight:700, marginBottom:12 }}>Fee Breakdown</div>
          {[
            ['iPay Processing Fee', s.ipay_fee??0,          '#ef4444'],
            ['Platform Commission', s.platform_fee??0,      '#f97316'],
            ['Delivery Commission', e.delivery_commission??0,'#f59e0b'],
          ].map(([l,v,c])=><BarRow key={l} label={l} value={v} max={fees||1} color={c} sub={fees>0?pct(v/fees*100):''} />)}
        </div>
        <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'16px 18px' }}>
          <div style={{ fontSize:12, fontWeight:700, marginBottom:12 }}>Payout Sources</div>
          {[
            ['Products',  e.product_payout??0,  '#15803d'],
            ['Delivery',  e.delivery_payout??0, '#0369a1'],
            ['Tips',      e.tips_total??0,       '#7c3aed'],
          ].map(([l,v,c])=><BarRow key={l} label={l} value={v} max={(s.total_payout||1)} color={c} sub={s.total_payout>0?pct(v/s.total_payout*100):''} />)}
        </div>
      </div>

      <div style={{ display:'flex', gap:10, marginTop:18 }}>
        <ActionBtn label="⬇ Download CSV" onClick={csv} color="#1d4ed8" />
        <ActionBtn label="🖨 Print / PDF"  onClick={()=>printReport('P&L Summary',dateFrom,dateTo)} color="#374151" />
      </div>
    </div>
  )
}

// ── Sales Performance ─────────────────────────────────────────────────────────
function ReportSales({ dateFrom, dateTo }) {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true)
  useEffect(() => {
    setLoading(true)
    api.get(`/analytics/shop?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(r => setData(r.data)).catch(()=>{}).finally(()=>setLoading(false))
  }, [dateFrom, dateTo])

  if (loading) return <Loader />
  const s = data?.summary || {}

  const csv = () => downloadCSV(`sales-${dateFrom}-${dateTo}.csv`, [
    ['SALES PERFORMANCE', `${fmtD(dateFrom)} – ${fmtD(dateTo)}`],
    [],
    ['Metric','Value'],
    ['Gross Revenue (RWF)', s.revenue??0],
    ['Total Orders',        s.orders??0],
    ['Units Sold',          s.units_sold??0],
    ['Avg Order Value (RWF)',s.avg_order??0],
    ['Paid Orders',         s.paid_orders??0],
    ['Unpaid Orders',       s.unpaid_orders??0],
    ['Delivered Orders',    s.delivered??0],
    [],
    ['REVENUE BY DAY'],
    ['Label','Revenue (RWF)'],
    ...(data?.revenue_chart||[]).map(r=>[r.label, r.value]),
  ])

  return (
    <div id="report-content">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Revenue"       value={rwf(s.revenue)}    accent="#e8440a" bold />
        <Stat label="Orders"        value={num(s.orders)}     accent="#111317" />
        <Stat label="Units Sold"    value={num(s.units_sold)} accent="#7c3aed" />
        <Stat label="Avg Order"     value={rwf(s.avg_order)}  accent="#111317" />
        <Stat label="Paid Orders"   value={num(s.paid_orders??0)} accent="#15803d" sub={s.orders>0?pct((s.paid_orders??0)/s.orders*100):'—'} />
        <Stat label="Delivered"     value={num(s.delivered??0)}   accent="#0369a1" sub={s.orders>0?pct((s.delivered??0)/s.orders*100):'—'} />
      </div>
      <MiniChart data={data?.revenue_chart||[]} color="#e8440a" label="Daily Revenue" />
      {data?.revenue_chart?.length > 0 && (
        <div style={{ marginTop:16, border:'1px solid #e9e9e4', borderRadius:10, overflow:'hidden' }}>
          <div style={{ padding:'9px 16px', background:'#f9f9f7', borderBottom:'1px solid #e9e9e4', display:'grid', gridTemplateColumns:'1fr 1fr' }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace" }}>Period</div>
            <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace" }}>Revenue</div>
          </div>
          {(data?.revenue_chart||[]).map((r,i)=>(
            <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', padding:'9px 16px', borderBottom:i<(data.revenue_chart.length-1)?'1px solid #f3f3f0':'none' }}>
              <div style={{ fontSize:12, fontFamily:"'JetBrains Mono',monospace" }}>{r.label}</div>
              <div style={{ fontSize:12, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", color:'#e8440a' }}>{rwf(r.value)}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display:'flex', gap:10, marginTop:18 }}>
        <ActionBtn label="⬇ Download CSV" onClick={csv} color="#1d4ed8" />
        <ActionBtn label="🖨 Print / PDF"  onClick={()=>printReport('Sales Performance',dateFrom,dateTo)} color="#374151" />
      </div>
    </div>
  )
}

// ── Product Performance ───────────────────────────────────────────────────────
function ReportProducts({ dateFrom, dateTo }) {
  const [data,     setData]     = useState(null)
  const [products, setProducts] = useState([])
  const [loading,  setLoading]  = useState(true)
  useEffect(() => {
    setLoading(true)
    Promise.all([
      api.get(`/analytics/shop?date_from=${dateFrom}&date_to=${dateTo}`),
      api.get(`/products/my-products?limit=200`),
    ]).then(([a,p])=>{ setData(a.data); setProducts(p.data.products||p.data||[]) })
      .catch(()=>{}).finally(()=>setLoading(false))
  }, [dateFrom, dateTo])

  if (loading) return <Loader />
  const top      = data?.top_products || []
  const topIds   = new Set(top.map(p=>String(p.prod_id)))
  const slow     = products.filter(p=>!topIds.has(String(p.prod_id)))
  const outStock = products.filter(p=>(p.quantity??0)===0)
  const lowStock = products.filter(p=>(p.quantity??0)>0&&(p.quantity??0)<=5)

  const csv = () => downloadCSV(`products-${dateFrom}-${dateTo}.csv`, [
    ['PRODUCT PERFORMANCE', `${fmtD(dateFrom)} – ${fmtD(dateTo)}`],
    [],
    ['TOP SELLERS'],
    ['Rank','Product','Units Sold','Revenue (RWF)','% of Total'],
    ...top.map((p,i)=>[i+1,p.name,p.units_sold,p.revenue,`${p.pct}%`]),
    [],
    ['SLOW MOVERS (no sales this period)'],
    ['Product','Current Stock'],
    ...slow.map(p=>[p.name, p.quantity??0]),
    [],
    ['OUT OF STOCK'],
    ['Product'],
    ...outStock.map(p=>[p.name]),
  ])

  return (
    <div id="report-content">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Total Products"  value={num(products.length)}  accent="#111317" />
        <Stat label="Active Sellers"  value={num(top.length)}       accent="#15803d" sub="had sales this period" />
        <Stat label="Slow Movers"     value={num(slow.length)}      accent={slow.length>0?'#b45309':'#9a9a94'} sub="no sales this period" />
        <Stat label="Out of Stock"    value={num(outStock.length)}  accent={outStock.length>0?'#b91c1c':'#9a9a94'} />
        <Stat label="Low Stock (≤5)"  value={num(lowStock.length)}  accent={lowStock.length>0?'#b45309':'#9a9a94'} />
      </div>

      <div style={{ fontSize:13, fontWeight:800, marginBottom:12 }}>Top Sellers</div>
      <Table
        cols={[
          { key:'rank',      label:'#',       w:'40px',  render:(_,i)=>i+1 },
          { key:'name',      label:'Product', w:'2fr'   },
          { key:'units_sold',label:'Units',   w:'80px', mono:true, bold:true },
          { key:'revenue',   label:'Revenue', w:'1fr',  mono:true, bold:true, render:r=>rwf(r.revenue), color:()=>'#15803d' },
          { key:'pct',       label:'Share',   w:'80px', render:r=>(
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ flex:1, height:6, background:'#f3f3f0', borderRadius:3 }}>
                <div style={{ height:'100%', width:`${r.pct}%`, background:'#e8440a', borderRadius:3 }} />
              </div>
              <span style={{ fontSize:10, fontFamily:"'JetBrains Mono',monospace" }}>{r.pct}%</span>
            </div>
          )},
        ]}
        rows={top}
        emptyMsg="No product sales in this period."
      />

      {slow.length > 0 && (
        <>
          <div style={{ fontSize:13, fontWeight:800, margin:'20px 0 12px', color:'#b45309' }}>⚠ Slow Movers — No Sales This Period</div>
          <Table
            cols={[
              { key:'name',     label:'Product',       w:'2fr' },
              { key:'quantity', label:'Stock',         w:'100px', mono:true, render:r=>r.quantity??0,
                color:r=>(r.quantity??0)===0?'#b91c1c':(r.quantity??0)<=5?'#b45309':'#374151' },
              { key:'status',   label:'Status',        w:'100px', render:r=>(
                <span style={{ fontSize:10, fontWeight:700, fontFamily:"'JetBrains Mono',monospace",
                  color:(r.quantity??0)===0?'#b91c1c':'#b45309',
                  background:(r.quantity??0)===0?'#fef2f2':'#fffbeb',
                  padding:'2px 6px', borderRadius:4 }}>
                  {(r.quantity??0)===0?'OUT OF STOCK':`LOW (${r.quantity})`}
                </span>
              )},
            ]}
            rows={slow}
          />
        </>
      )}

      <div style={{ display:'flex', gap:10, marginTop:18 }}>
        <ActionBtn label="⬇ Download CSV" onClick={csv} color="#1d4ed8" />
        <ActionBtn label="🖨 Print / PDF"  onClick={()=>printReport('Product Performance',dateFrom,dateTo)} color="#374151" />
      </div>
    </div>
  )
}

// ── Order Health ──────────────────────────────────────────────────────────────
function ReportOrders({ dateFrom, dateTo }) {
  const [data, setData] = useState(null); const [orders, setOrders] = useState([]); const [loading, setLoading] = useState(true)
  useEffect(() => {
    setLoading(true)
    Promise.all([
      api.get(`/analytics/shop?date_from=${dateFrom}&date_to=${dateTo}`),
      api.get(`/orders/seller/incoming?page=1&limit=200&date_from=${dateFrom}&date_to=${dateTo}`),
    ]).then(([a,o])=>{ setData({ s:a.data.summary||{}, stats:o.data.stats||{} }); setOrders(o.data.orders||[]) })
      .catch(()=>{}).finally(()=>setLoading(false))
  }, [dateFrom, dateTo])

  if (loading) return <Loader />
  const { s, stats } = data || { s:{}, stats:{} }
  const total       = stats.all ?? s.orders ?? 0
  const cancelled   = stats.cancelled ?? 0
  const cancelRate  = total > 0 ? cancelled/total*100 : 0
  const fulfil      = stats.delivered ?? s.delivered ?? 0
  const fulfilRate  = total > 0 ? fulfil/total*100 : 0
  const payRate     = total > 0 ? (s.paid_orders??0)/total*100 : 0

  const STATUS_COLOR = { pending:'#f59e0b', confirmed:'#3b82f6', processing:'#8b5cf6', shipped:'#0ea5e9', delivered:'#16a34a', cancelled:'#ef4444' }

  const csv = () => downloadCSV(`orders-${dateFrom}-${dateTo}.csv`, [
    ['ORDER HEALTH', `${fmtD(dateFrom)} – ${fmtD(dateTo)}`],
    [],
    ['Metric','Value'],
    ['Total Orders',       total],
    ['Delivered',          fulfil],
    ['Cancellations',      cancelled],
    ['Cancellation Rate',  cancelRate.toFixed(2)+'%'],
    ['Fulfilment Rate',    fulfilRate.toFixed(2)+'%'],
    ['Paid Orders',        s.paid_orders??0],
    ['Payment Success Rate', payRate.toFixed(2)+'%'],
    ['Unpaid Orders',      s.unpaid_orders??0],
    ['Failed Payments',    s.failed_orders??0],
    [],
    ['STATUS BREAKDOWN'],
    ['Status','Count','%'],
    ...Object.entries(stats).filter(([k])=>k!=='all'&&stats[k]>0)
      .map(([k,v])=>[k, v, total>0?(v/total*100).toFixed(1)+'%':'0%']),
    ...(orders.length>0?[
      [],['ORDERS'],
      ['Order ID','Buyer','Status','Total (RWF)','Payment','Date'],
      ...orders.map(o=>[o.order_id, o.buyer_name||'—', o.status||'—', o.shop_total||o.total_amount||0, o.payment_method||'—', o.created_at?.slice(0,10)||'—']),
    ]:[]),
  ])

  return (
    <div id="report-content">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Total Orders"      value={num(total)}          accent="#111317" />
        <Stat label="Cancellation Rate" value={pct(cancelRate)}     accent={cancelRate<5?'#15803d':cancelRate<15?'#b45309':'#b91c1c'} sub={`${num(cancelled)} cancelled`} />
        <Stat label="Fulfilment Rate"   value={pct(fulfilRate)}     accent={fulfilRate>80?'#15803d':fulfilRate>50?'#b45309':'#b91c1c'} sub={`${num(fulfil)} delivered`} />
        <Stat label="Payment Success"   value={pct(payRate)}        accent={payRate>80?'#15803d':payRate>60?'#b45309':'#b91c1c'} sub={`${num(s.paid_orders??0)} paid`} />
        <Stat label="Unpaid"            value={num(s.unpaid_orders??0)} accent={s.unpaid_orders>0?'#b45309':'#9a9a94'} />
        <Stat label="Failed Payments"   value={num(s.failed_orders??0)} accent={s.failed_orders>0?'#b91c1c':'#9a9a94'} />
      </div>

      <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'16px 18px', marginBottom:20 }}>
        <div style={{ fontSize:12, fontWeight:700, marginBottom:14 }}>Order Status Breakdown</div>
        {Object.entries(stats).filter(([k])=>k!=='all'&&stats[k]>0)
          .sort((a,b)=>b[1]-a[1])
          .map(([k,v])=>(
          <BarRow key={k} label={k.charAt(0).toUpperCase()+k.slice(1)} value={v}
            max={total} color={STATUS_COLOR[k]??'#9ca3af'}
            sub={total>0?`${pct(v/total*100)} of orders`:''} />
        ))}
      </div>

      <div style={{ fontSize:13, fontWeight:800, margin:'20px 0 12px' }}>Orders</div>
      <Table
        cols={[
          { key:'order_id',   label:'Order #',  w:'80px',  mono:true },
          { key:'buyer_name', label:'Buyer',     w:'2fr' },
          { key:'status',     label:'Status',    w:'110px', render:r=>(
            <span style={{ fontSize:10, fontWeight:700,
              color:STATUS_COLOR[r.status?.toLowerCase()]||'#9ca3af',
              background:(STATUS_COLOR[r.status?.toLowerCase()]||'#9ca3af')+'22',
              padding:'2px 6px', borderRadius:4, fontFamily:"'JetBrains Mono',monospace" }}>
              {r.status?.toUpperCase()||'—'}
            </span>
          )},
          { key:'shop_total', label:'Total',     w:'1fr',   mono:true, bold:true, render:r=>rwf(r.shop_total||r.total_amount||0), color:()=>'#374151' },
          { key:'payment_method', label:'Payment', w:'90px', render:r=>r.payment_method?.toUpperCase()||'—' },
          { key:'created_at', label:'Date',       w:'100px', render:r=>r.created_at?.slice(0,10)||'—' },
        ]}
        rows={orders}
        emptyMsg="No orders in this period."
      />

      <div style={{ display:'flex', gap:10, marginTop:18 }}>
        <ActionBtn label="⬇ Download CSV" onClick={csv} color="#1d4ed8" />
        <ActionBtn label="🖨 Print / PDF"  onClick={()=>printReport('Order Health',dateFrom,dateTo)} color="#374151" />
      </div>
    </div>
  )
}

// ── Delivery Report ───────────────────────────────────────────────────────────
function ReportDelivery({ dateFrom, dateTo }) {
  const { seller } = useAuth()
  const sellerLabel = seller?.shop_name || seller?.name || 'Seller'
  const [orders, setOrders] = useState([]); const [loading, setLoading] = useState(true)
  useEffect(() => {
    setLoading(true)
    api.get(`/orders/seller/incoming?page=1&limit=200&date_from=${dateFrom}&date_to=${dateTo}`)
      .then(r => setOrders(r.data.orders||[])).catch(()=>{}).finally(()=>setLoading(false))
  }, [dateFrom, dateTo])

  if (loading) return <Loader />
  const shipped  = orders.filter(o=>['shipped','delivered'].includes(o.status?.toLowerCase()))
  const selfDel  = orders.filter(o=>o.is_self_delivery)
  const drvDel   = orders.filter(o=>o.driver_id&&!o.is_self_delivery)
  const failed   = orders.filter(o=>o.delivery_status==='failed')
  const total    = shipped.length

  const csv = () => downloadCSV(`delivery-${dateFrom}-${dateTo}.csv`, [
    ['DELIVERY REPORT', `${fmtD(dateFrom)} – ${fmtD(dateTo)}`],
    [],
    ['Metric','Count','%'],
    ['Shipped/Delivered', total, '100%'],
    ['Self Delivery',     selfDel.length, total>0?pct(selfDel.length/total*100):'0%'],
    ['Driver Delivery',   drvDel.length,  total>0?pct(drvDel.length/total*100):'0%'],
    ['Delivery Failures', failed.length,  total>0?pct(failed.length/total*100):'0%'],
    [],
    ['ORDER DETAILS'],
    ['Order ID','Buyer','Status','Delivery Type','Delivery Status','Driver','Driver Company','Driver Note'],
    ...shipped.map(o=>[
      o.order_id, o.buyer_name||'—', o.status, o.is_self_delivery?'Self':'Driver',
      o.delivery_status||'—',
      o.is_self_delivery ? sellerLabel : (o.driver_name||'—'),
      o.is_self_delivery ? '—' : (o.driver_company||'—'),
      o.driver_note||'—',
    ]),
  ])

  return (
    <div id="report-content">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Shipped + Delivered"  value={num(total)}         accent="#111317" />
        <Stat label="Self Delivery"        value={num(selfDel.length)} accent="#e8440a" sub={total>0?pct(selfDel.length/total*100):''} />
        <Stat label="Driver Delivery"      value={num(drvDel.length)}  accent="#0369a1" sub={total>0?pct(drvDel.length/total*100):''} />
        <Stat label="Delivery Failures"    value={num(failed.length)}  accent={failed.length>0?'#b91c1c':'#15803d'} sub={total>0?pct(failed.length/total*100):''} />
      </div>

      {failed.length > 0 && (
        <>
          <div style={{ fontSize:13, fontWeight:800, color:'#b91c1c', marginBottom:12 }}>⚠ Failed Deliveries</div>
          <Table
            cols={[
              { key:'order_id',       label:'Order #',     w:'80px',  mono:true },
              { key:'buyer_name',     label:'Buyer',       w:'1.2fr' },
              { key:'driver_name',    label:'Driver',      w:'1fr',   render:r=>r.is_self_delivery?`🛵 ${sellerLabel}`:(r.driver_name||'—') },
              { key:'driver_company', label:'Company',     w:'1fr',   render:r=>r.is_self_delivery?'—':(r.driver_company||'—') },
              { key:'driver_note',    label:'Driver Note', w:'2fr',   render:r=>r.driver_note||'—' },
              { key:'delivery_status',label:'Status',      w:'100px', render:r=>(
                <span style={{ fontSize:10, fontWeight:700, color:'#b91c1c', background:'#fef2f2', padding:'2px 6px', borderRadius:4, fontFamily:"'JetBrains Mono',monospace" }}>FAILED</span>
              )},
            ]}
            rows={failed}
          />
        </>
      )}

      <div style={{ fontSize:13, fontWeight:800, margin:'20px 0 12px' }}>All Delivery Orders</div>
      <Table
        cols={[
          { key:'order_id',      label:'Order #',   w:'70px',  mono:true },
          { key:'buyer_name',    label:'Buyer',     w:'1.2fr'  },
          { key:'type',          label:'Type',      w:'90px',  render:r=>r.is_self_delivery?'🛵 Self':'🚗 Driver' },
          { key:'status',        label:'Order',     w:'90px',  render:r=><span style={{ fontSize:10, fontWeight:700, color:'#15803d', fontFamily:"'JetBrains Mono',monospace" }}>{r.status}</span> },
          { key:'delivery_status',label:'Delivery', w:'100px', mono:true, render:r=>r.delivery_status||'—' },
          { key:'driver_name',   label:'Driver',    w:'1fr',   render:r=>r.is_self_delivery?`🛵 ${sellerLabel}`:(r.driver_name||'—') },
          { key:'driver_company',label:'Company',   w:'1fr',   render:r=>r.is_self_delivery?'—':(r.driver_company||'—') },
        ]}
        rows={shipped}
        emptyMsg="No shipped/delivered orders in this period."
      />

      <div style={{ display:'flex', gap:10, marginTop:18 }}>
        <ActionBtn label="⬇ Download CSV" onClick={csv} color="#1d4ed8" />
        <ActionBtn label="🖨 Print / PDF"  onClick={()=>printReport('Delivery Report',dateFrom,dateTo)} color="#374151" />
      </div>
    </div>
  )
}

// ── Earnings & Payout ─────────────────────────────────────────────────────────
function ReportEarnings({ dateFrom, dateTo }) {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true)
  useEffect(() => {
    setLoading(true)
    api.get(`/orders/earnings-detail?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(r=>setData(r.data)).catch(()=>{}).finally(()=>setLoading(false))
  }, [dateFrom, dateTo])

  if (loading) return <Loader />
  const t       = data?.totals   || {}
  const orders  = data?.orders   || []
  const tips    = data?.tips     || []
  const commPct = data?.driver_comm_pct || 0
  const tierDiscount  = data?.tier_discount          ?? 0
  const tierName      = data?.tier                   ?? 'bronze'
  const platBase      = data?.platform_pct_base      ?? 0
  const platEffective = data?.platform_pct_effective ?? platBase
  const ipayPct       = data?.ipay_pct               ?? 0
  const tierSaving    = +(Number(t.gross_subtotal || 0) * tierDiscount).toFixed(2)
  const tipsGross     = t.tips_gross_total    ?? t.tips_total ?? 0
  const tipsPlatform  = t.tips_platform_total ?? 0
  const tipsNet       = t.tips_total          ?? 0
  const tipsIpay      = t.tips_ipay_total     ?? +(tipsGross - tipsPlatform - tipsNet).toFixed(2)

  const TIER_ICONS = { bronze:'🥉', silver:'🥈', gold:'🥇', platinum:'💎' }

  const csv = () => downloadCSV(`earnings-${dateFrom}-${dateTo}.csv`, [
    ['EARNINGS & PAYOUT', `${fmtD(dateFrom)} – ${fmtD(dateTo)}`],
    [],
    ['SUMMARY'],
    ['Tier',                    `${TIER_ICONS[tierName]||''} ${tierName}`.trim()],
    ['Platform Fee % (Base)',   `${(platBase * 100).toFixed(0)}%`],
    ['Platform Fee % (Effective)', `${(platEffective * 100).toFixed(0)}%`],
    ...(tierDiscount > 0 ? [['Tier Saving (RWF)', tierSaving]] : []),
    ['Gross Subtotal (RWF)', t.gross_subtotal??0],
    ['Product Payout (RWF)', t.product_payout??0],
    ['Delivery Gross (RWF)', t.delivery_gross??0],
    ['Delivery Commission (RWF)', t.delivery_commission??0],
    ['Delivery Payout (RWF)', t.delivery_payout??0],
    ['Tips Gross (RWF)',        tipsGross],
    ['Tips iPay Fee (RWF)',     tipsIpay],
    ['Tips Platform Fee (RWF)', tipsPlatform],
    ['Tips Net Payout (RWF)',   tipsNet],
    ['TOTAL PAYOUT (RWF)',    t.total_payout??0],
    [],
    ['PER-ORDER DETAIL'],
    ['Order ID','Buyer','Date','Subtotal','iPay Fee',`Platform Fee (${(platEffective*100).toFixed(0)}%)`, 'Product Payout','Delivery Gross','Delivery Comm','Delivery Payout','Payment Method'],
    ...orders.map(o=>[
      o.order_id, o.buyer_name||'—', o.created_at?.slice(0,10)||'—',
      o.shop_subtotal||0, o.ipay_fee||0, o.platform_fee||0, o.product_payout||0,
      o.delivery_gross||0, o.delivery_commission||0, o.delivery_payout||0,
      o.payment_method||'—',
    ]),
    ...(tips.length>0?[
      [],['TIPS'],
      ['Buyer','Gross Tip (RWF)',`iPay Fee ${(ipayPct*100).toFixed(0)}% (RWF)`,'Platform Fee (RWF)','Net Payout (RWF)','Emoji','Date','Payment Method'],
      ...tips.map(tip=>{
        const g=Number(tip.gross_amount||tip.amount||0)
        const pf=Number(tip.platform_fee||0)
        const f=Number(tip.ipay_fee||+(g*ipayPct).toFixed(2))
        const n=Number(tip.net_amount||g-f-pf)
        return [tip.buyer_name||'—',g,f,pf,n,tip.emoji||'—',tip.created_at?.slice(0,10)||'—',tip.payment_method||'—']
      }),
    ]:[]),
  ])

  return (
    <div id="report-content">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Gross Subtotal"    value={rwf(t.gross_subtotal??0)}    accent="#111317" />
        <Stat label="Product Payout"    value={rwf(t.product_payout??0)}    accent="#15803d"
          sub={tierDiscount > 0
            ? `${(platEffective*100).toFixed(0)}% platform fee (${TIER_ICONS[tierName]} ${tierName} · saved ${rwf(tierSaving)})`
            : `${(platBase*100).toFixed(0)}% platform fee`} />
        <Stat label="Delivery Payout"   value={rwf(t.delivery_payout??0)}   accent="#0369a1" sub={`${pct(commPct)} driver commission`} />
        <Stat label="Tips Net Payout"    value={rwf(tipsNet)}                accent="#7c3aed"
          sub={ipayPct > 0
            ? `${t.tips_count??0} tips · gross ${rwf(tipsGross)} − iPay ${rwf(tipsIpay)} − plat ${rwf(tipsPlatform)}`
            : `${t.tips_count??0} tips`} />
        <Stat label="TOTAL PAYOUT"      value={rwf(t.total_payout??0)}      accent="#15803d" bold />
      </div>

      {tierDiscount > 0 && (
        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderLeft:'3px solid #15803d', borderRadius:8, padding:'10px 16px', marginBottom:18, fontSize:12, color:'#374151', fontFamily:"'JetBrains Mono',monospace", display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <span style={{ fontSize:16 }}>{TIER_ICONS[tierName]}</span>
          <span><b style={{ color:'#15803d' }}>{tierName.charAt(0).toUpperCase()+tierName.slice(1)} tier discount applied</b> — platform fee {(platEffective*100).toFixed(0)}% <span style={{ color:'#9a9a94' }}>(base {(platBase*100).toFixed(0)}% − {(tierDiscount*100).toFixed(0)}pp discount)</span> · you saved <b style={{ color:'#15803d' }}>{rwf(tierSaving)}</b> this period</span>
          {data?.is_overridden && <span style={{ fontSize:9, fontWeight:700, color:'#b45309', background:'#fffbeb', border:'1px solid #fde68a', padding:'1px 6px', borderRadius:3 }}>ADMIN SET</span>}
        </div>
      )}

      <div style={{ fontSize:13, fontWeight:800, marginBottom:12 }}>Per-Order Payout</div>
      <Table
        cols={[
          { key:'order_id',       label:'Order #',     w:'70px',  mono:true },
          { key:'buyer_name',     label:'Buyer',       w:'1.2fr' },
          { key:'shop_subtotal',  label:'Subtotal',    w:'1fr',   mono:true, render:r=>rwf(r.shop_subtotal) },
          { key:'ipay_fee',       label:'iPay',        w:'80px',  mono:true, render:r=>rwf(r.ipay_fee),       color:()=>'#ef4444' },
          { key:'platform_fee',   label:platEffective > 0 ? `Pltf ${(platEffective*100).toFixed(0)}%` : 'Platform', w:'80px',  mono:true, render:r=>rwf(r.platform_fee),   color:()=>'#f97316' },
          { key:'product_payout', label:'Payout',      w:'1fr',   mono:true, render:r=>rwf(r.product_payout), color:()=>'#15803d', bold:true },
          { key:'delivery_payout',label:'+ Delivery',  w:'1fr',   mono:true, render:r=>r.delivery_payout>0?rwf(r.delivery_payout):'—', color:r=>r.delivery_payout>0?'#0369a1':'#9ca3af' },
        ]}
        rows={orders}
        emptyMsg="No paid orders in this period."
      />

      {tips.length > 0 && (
        <>
          <div style={{ fontSize:13, fontWeight:800, margin:'20px 0 12px' }}>Tips Received</div>
          <Table
            cols={[
              { key:'buyer_name',    label:'Buyer',   w:'1.6fr' },
              { key:'amount',        label:'Gross',   w:'0.9fr', mono:true, render:r=>rwf(r.amount), color:()=>'#374151' },
              { key:'platform_fee',  label:'Platform',w:'0.9fr', mono:true, render:r=>rwf(r.platform_fee), color:()=>'#f97316' },
              { key:'net_amount',    label:'Net',     w:'0.9fr', mono:true, bold:true, render:r=>rwf(r.net_amount ?? r.amount), color:()=>'#7c3aed' },
              { key:'emoji',         label:'Emoji',   w:'60px', render:r=>r.emoji||'—' },
              { key:'payment_method',label:'Payment', w:'1fr',  render:r=>r.payment_method?.toUpperCase()||'—' },
            ]}
            rows={tips}
          />
        </>
      )}

      <div style={{ display:'flex', gap:10, marginTop:18 }}>
        <ActionBtn label="⬇ Download CSV" onClick={csv} color="#1d4ed8" />
        <ActionBtn label="🖨 Print / PDF"  onClick={()=>printReport('Earnings & Payout',dateFrom,dateTo)} color="#374151" />
      </div>
    </div>
  )
}

// ── Inventory Report ──────────────────────────────────────────────────────────
function ReportInventory() {
  const [products, setProducts] = useState([]); const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  useEffect(() => {
    setLoading(true)
    api.get('/products/my-products?limit=200').then(r=>setProducts(r.data.products||r.data||[])).catch(()=>{}).finally(()=>setLoading(false))
  }, [])

  if (loading) return <Loader />
  const filtered  = products.filter(p=>!search||p.name?.toLowerCase().includes(search.toLowerCase()))
  const outStock  = products.filter(p=>(p.quantity??0)===0)
  const lowStock  = products.filter(p=>(p.quantity??0)>0&&(p.quantity??0)<=5)
  const healthy   = products.filter(p=>(p.quantity??0)>5)

  const csv = () => downloadCSV('inventory.csv', [
    ['INVENTORY REPORT', new Date().toLocaleDateString()],
    [],
    ['Product','Category','Stock','Status','Price (RWF)'],
    ...products.map(p=>[
      p.name, p.category||'—', p.quantity??0,
      (p.quantity??0)===0?'OUT OF STOCK':(p.quantity??0)<=5?'LOW STOCK':'OK',
      p.price||0,
    ]),
  ])

  const stockColor = (q) => q===0?'#b91c1c':q<=5?'#b45309':'#15803d'
  const stockLabel = (q) => q===0?'OUT OF STOCK':q<=5?`LOW (${q})`:`${q} in stock`

  return (
    <div id="report-content">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Total Products" value={num(products.length)} accent="#111317" />
        <Stat label="Out of Stock"   value={num(outStock.length)} accent={outStock.length>0?'#b91c1c':'#15803d'} sub="need restocking" />
        <Stat label="Low Stock (≤5)" value={num(lowStock.length)} accent={lowStock.length>0?'#b45309':'#15803d'} sub="running low" />
        <Stat label="Healthy Stock"  value={num(healthy.length)}  accent="#15803d" />
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, background:'#f6f6f3', border:'1px solid #e9e9e4', borderRadius:8, padding:'8px 12px' }}>
        <span style={{ fontSize:14, color:'#9a9a94' }}>🔍</span>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search products…"
          style={{ border:'none', outline:'none', background:'transparent', fontSize:13, flex:1, fontFamily:"'Plus Jakarta Sans',sans-serif" }} />
      </div>

      <Table
        cols={[
          { key:'name',     label:'Product',  w:'2fr' },
          { key:'category', label:'Category', w:'1fr',  render:r=>r.category||'—' },
          { key:'price',    label:'Price',    w:'100px', mono:true, bold:true, render:r=>rwf(r.price??0), color:()=>'#374151' },
          { key:'quantity', label:'Stock',    w:'120px', render:r=>(
            <span style={{ fontSize:11, fontWeight:800, color:stockColor(r.quantity??0), background:stockColor(r.quantity??0)+'18', padding:'2px 8px', borderRadius:5, fontFamily:"'JetBrains Mono',monospace" }}>
              {stockLabel(r.quantity??0)}
            </span>
          )},
        ]}
        rows={filtered}
        emptyMsg={search ? 'No products match your search.' : 'No products found.'}
      />
      <div style={{ display:'flex', gap:10, marginTop:18 }}>
        <ActionBtn label="⬇ Download CSV" onClick={csv} color="#1d4ed8" />
        <ActionBtn label="🖨 Print / PDF"  onClick={()=>printReport('Inventory Report', '—', '—')} color="#374151" />
      </div>
    </div>
  )
}

// ── Live Streaming ────────────────────────────────────────────────────────────
function ReportLive({ dateFrom, dateTo }) {
  const { seller }  = useAuth()
  const shopId      = seller?.shopId
  const [summary,   setSummary]  = useState(null)
  const [history,   setHistory]  = useState([])
  const [loading,   setLoading]  = useState(true)

  useEffect(() => {
    if (!shopId) { setLoading(false); return }
    setLoading(true)
    Promise.all([
      api.get(`/live-history/${shopId}/summary`),
      api.get(`/live-history/${shopId}?limit=50`),
    ]).then(([s,h])=>{ setSummary(s.data); setHistory(h.data.history||[]) })
      .catch(()=>{}).finally(()=>setLoading(false))
  }, [shopId])

  const fmtDur = (secs) => {
    if (!secs) return '—'
    const h = Math.floor(secs/3600), m = Math.floor((secs%3600)/60)
    return h > 0 ? `${h}h ${m}m` : `${m}m`
  }

  if (loading) return <Loader />
  const s = summary || {}

  const csv = () => downloadCSV('live-streaming.csv', [
    ['LIVE STREAMING REPORT'],
    [],
    ['SUMMARY'],
    ['Total Streams',       s.totalStreams??0],
    ['Total Time Live',     s.totalDurationFormatted||'—'],
    ['Avg Peak Viewers',    s.avgPeakViewers??0],
    ['All-Time Peak',       s.allTimePeakViewers??0],
    ['Total Messages',      s.totalMessages??0],
    [],
    ['STREAM HISTORY'],
    ['Date','Duration','Peak Viewers','Total Messages','Channel'],
    ...history.map(r=>[
      r.started_at?.slice(0,10)||'—', r.duration_formatted||fmtDur(r.duration_secs),
      r.peak_viewers||0, r.total_messages||0, r.agora_channel||'—',
    ]),
  ])

  return (
    <div id="report-content">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Total Streams"     value={num(s.totalStreams??0)}           accent="#111317" />
        <Stat label="Time Live"         value={s.totalDurationFormatted||'—'}   accent="#b91c1c" />
        <Stat label="Avg Peak Viewers"  value={num(s.avgPeakViewers??0)}         accent="#e8440a" />
        <Stat label="All-Time Peak"     value={num(s.allTimePeakViewers??0)}     accent="#e8440a" sub="highest single stream" />
        <Stat label="Total Messages"    value={num(s.totalMessages??0)}          accent="#7c3aed" sub="viewer engagement" />
      </div>

      <div style={{ fontSize:13, fontWeight:800, marginBottom:12 }}>Stream History</div>
      <Table
        cols={[
          { key:'started_at',    label:'Date',      w:'100px', render:r=>r.started_at?.slice(0,10)||'—' },
          { key:'duration',      label:'Duration',  w:'90px',  render:r=>r.duration_formatted||fmtDur(r.duration_secs) },
          { key:'peak_viewers',  label:'Peak',      w:'80px',  mono:true, bold:true, render:r=>num(r.peak_viewers??0), color:()=>'#e8440a' },
          { key:'total_messages',label:'Messages',  w:'90px',  mono:true, render:r=>num(r.total_messages??0), color:()=>'#7c3aed' },
        ]}
        rows={history}
        emptyMsg={shopId?'No streams recorded yet.':'Shop not found.'}
      />
      <div style={{ display:'flex', gap:10, marginTop:18 }}>
        <ActionBtn label="⬇ Download CSV" onClick={csv} color="#1d4ed8" />
        <ActionBtn label="🖨 Print / PDF"  onClick={()=>printReport('Live Streaming Report',dateFrom,dateTo)} color="#374151" />
      </div>
    </div>
  )
}

// ── Shared utility components ─────────────────────────────────────────────────
function Loader() {
  return <div style={{ textAlign:'center', color:'#9a9a94', fontSize:13, padding:'48px 0' }}>Loading report…</div>
}

function ActionBtn({ label, onClick, color }) {
  const [hov, setHov] = useState(false)
  return (
    <button onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ height:40, padding:'0 18px', borderRadius:9, border:'none', background:hov?color+'dd':color, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", transition:'background 0.15s' }}>
      {label}
    </button>
  )
}

// ── Report renderer ───────────────────────────────────────────────────────────
function ReportView({ reportId, dateFrom, dateTo }) {
  const props = { dateFrom, dateTo }
  switch (reportId) {
    case 'pnl':       return <ReportPNL       {...props} />
    case 'sales':     return <ReportSales     {...props} />
    case 'products':  return <ReportProducts  {...props} />
    case 'orders':    return <ReportOrders    {...props} />
    case 'delivery':  return <ReportDelivery  {...props} />
    case 'earnings':  return <ReportEarnings  {...props} />
    case 'inventory': return <ReportInventory />
    case 'live':      return <ReportLive      {...props} />
    default:          return null
  }
}

// ── Print CSS ─────────────────────────────────────────────────────────────────
const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #report-content, #report-content * { visibility: visible; }
  #report-content { position: absolute; left: 0; top: 0; width: 100%; }
  @page { margin: 16mm; }
}
`

// ── Main page ─────────────────────────────────────────────────────────────────
// ── Overview summary (shown on landing page) ─────────────────────────────────
function ReportOverview({ dateFrom, dateTo }) {
  const [data, setData]   = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    setLoading(true)
    Promise.all([
      api.get(`/analytics/shop?date_from=${dateFrom}&date_to=${dateTo}`),
      api.get(`/orders/earnings-detail?date_from=${dateFrom}&date_to=${dateTo}`),
      api.get(`/orders/seller/incoming?page=1&limit=1`),
    ]).then(([a, e, o]) => setData({ s: a.data.summary||{}, e: e.data.totals||{}, stats: o.data.stats||{} }))
      .catch(()=>{}).finally(()=>setLoading(false))
  }, [dateFrom, dateTo])

  if (loading) return (
    <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:28 }}>
      {[1,2,3,4,5,6].map(i=><div key={i} style={{ flex:1, minWidth:140, height:88, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, opacity:0.5 }} />)}
    </div>
  )

  const { s, e, stats } = data || {}
  const total       = stats?.all ?? s?.orders ?? 0
  const cancelled   = stats?.cancelled ?? 0
  const cancelRate  = total > 0 ? cancelled / total * 100 : 0
  const margin      = (s?.revenue ?? 0) > 0 ? ((s?.total_payout ?? 0) / s.revenue * 100) : 0

  return (
    <div style={{ marginBottom:28 }}>
      <div style={{ fontSize:12, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace", marginBottom:12 }}>
        Overview · {fmtD(dateFrom)} – {fmtD(dateTo)}
      </div>
      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
        <Stat label="Gross Revenue"     value={rwf(s?.revenue??0)}          accent="#111317" bold />
        <Stat label="Net Payout"        value={rwf(s?.total_payout??0)}     accent="#15803d" bold sub={`${pct(margin)} net margin`} />
        <Stat label="Total Orders"      value={num(total)}                  accent="#111317" />
        <Stat label="Units Sold"        value={num(s?.units_sold??0)}       accent="#7c3aed" />
        <Stat label="Cancellation Rate" value={pct(cancelRate)}             accent={cancelRate<5?'#15803d':cancelRate<15?'#b45309':'#b91c1c'} sub={`${num(cancelled)} cancelled`} />
        <Stat label="Tips Received"     value={rwf(e?.tips_total??0)}       accent="#7c3aed" sub={`${e?.tips_count??0} tips`} />
      </div>
    </div>
  )
}

export default function Reports() {
  const PRESETS = buildPresets()

  const [selected,     setSelected]     = useState(null)
  const [dateFrom,     setDateFrom]     = useState(PRESETS[0].from)
  const [dateTo,       setDateTo]       = useState(PRESETS[0].to)
  const [activePreset, setActivePreset] = useState(0)

  const applyPreset = (i) => { setActivePreset(i); setDateFrom(PRESETS[i].from); setDateTo(PRESETS[i].to) }

  const active = REPORTS.find(r => r.id === selected)

  return (
    <div style={{ padding:'28px 32px 80px', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
      <style>{PRINT_CSS}</style>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:22 }}>
        {selected && (
          <button onClick={()=>setSelected(null)}
            style={{ height:34, padding:'0 14px', borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", display:'flex', alignItems:'center', gap:6, color:'#374151' }}>
            ← Reports
          </button>
        )}
        <div>
          <div style={{ fontSize:22, fontWeight:800, letterSpacing:'-0.4px' }}>
            {selected ? `${active?.icon} ${active?.title}` : 'Reports'}
          </div>
          <div style={{ fontSize:13, color:'#9a9a94', marginTop:3 }}>
            {selected ? active?.desc : 'Business intelligence for your shop.'}
          </div>
        </div>
      </div>

      {/* ── Date range (shared for both overview and detail) ── */}
      {selected !== 'inventory' && (
        <DateRange
          dateFrom={dateFrom} dateTo={dateTo}
          onFrom={e=>{setDateFrom(e.target.value);setActivePreset(null)}}
          onTo={e=>{setDateTo(e.target.value);setActivePreset(null)}}
          presets={PRESETS} activePreset={activePreset} onPreset={applyPreset}
        />
      )}

      {/* ── Overview summary (landing page only) ── */}
      {!selected && (
        <ReportOverview dateFrom={dateFrom} dateTo={dateTo} />
      )}

      {/* ── Category grid (landing page) ── */}
      {!selected && (
        <>
          <div style={{ fontSize:12, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace", marginBottom:14 }}>
            Report Categories
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(250px,1fr))', gap:14 }}>
            {REPORTS.map(r => (
              <div key={r.id} onClick={()=>setSelected(r.id)}
                style={{ background:r.bg, border:`1.5px solid ${r.border}`, borderRadius:14, padding:'18px 20px', cursor:'pointer', transition:'transform 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.09)' }}
                onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none' }}>
                <div style={{ fontSize:26, marginBottom:8 }}>{r.icon}</div>
                <div style={{ fontSize:13, fontWeight:800, color:'#111317', marginBottom:3 }}>{r.title}</div>
                <div style={{ fontSize:12, color:'#6b7280', lineHeight:1.5 }}>{r.desc}</div>
                <div style={{ marginTop:12, fontSize:11, fontWeight:700, color:r.color }}>
                  View Full Report →
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Detailed report view ── */}
      {selected && (
        <ReportView reportId={selected} dateFrom={dateFrom} dateTo={dateTo} />
      )}
    </div>
  )
}
